import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import styles from "./edit-user-drawer-modal.module.scss";
import { Title } from "@src/components/atoms/title/title";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { RoleSelector } from "@src/components/molecules/roles-selector/roles-selector";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { Role, ZitadelUser, ZitadelUserType } from "@src/types/user-management";
import { UserStatusWithSwitch } from "@src/components/atoms/user-status-with-switch/user-status-with-switch";
import { TwoFactorAuthenticationButtonsContainer } from "@src/components/molecules/two-factor-authentication-buttons-wrapper/two-factor-authentication-buttons-wrapper";
import { DrawerButtonWrapper } from "@src/components/molecules/drawer-button-wrapper/drawer-button-wrapper";
import { Button } from "@src/components/atoms/buttons/button/button";
import { RootState } from "@src/reducers/store";
import { LanguageSelectorWithSearch } from "@src/components/molecules/language-selector-with-search/language-selector-with-search";
import { TimezoneSelectorWithSearch } from "@src/components/molecules/timezone-selector-with-search/timezone-selector-with-search";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

import { changeUserStatusUseCase } from "@src/cloud-connect/user-manager/use-cases/change-user-status-use-case";
import { trimUserInputs } from "@src/cloud-connect/user-manager/utils/trim-user-drawer-inputs";
import { validateUserInputs } from "@src/cloud-connect/user-manager/utils/validate-user-drawer-inputs";
import { patchUpdateUserUseCase } from "@src/cloud-connect/user-manager/use-cases/patch-update-user-use-case";
import {
  Language,
  Timezone,
  updateLanguage,
  updateModalsCloseDrawerModal,
  updateTimezone,
  updateUserListResultsReady,
} from "@src/reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import Spinner from "@src/components/atoms/spinner/spinner";
import { toISO2 } from "@src/cloud-connect/user-manager/utils/to-iso2";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { useI18N } from "@src/language/language-context";
import { getValidUserRolesUseCase } from "@src/cloud-connect/user-manager/use-cases/get-valid-user-roles-use-case";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";

const cssClassName = "edit-user-drawer-modal";

export const EditUserDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const selectedUserFromUserList: ZitadelUser | null = useSelector(
    (state: RootState) => state.yourSlice.selectedUserInEditUserDrawer,
  );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const [updatingUser, setUpdatingUser] = useState(false);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    editUserTitle: i18n("modules.editUserDrawerModal.editUserTitle"),
    userNameLabel: i18n("modules.editUserDrawerModal.userNameLabel"),
    userNamePlaceholder: i18n(
      "modules.editUserDrawerModal.userNamePlaceholder",
    ),
    givenNameLabel: i18n("modules.editUserDrawerModal.givenNameLabel"),
    givenNamePlaceholder: i18n(
      "modules.editUserDrawerModal.givenNamePlaceholder",
    ),
    familyNameLabel: i18n("modules.editUserDrawerModal.familyNameLabel"),
    familyNamePlaceholder: i18n(
      "modules.editUserDrawerModal.familyNamePlaceholder",
    ),
    emailLabel: i18n("modules.editUserDrawerModal.emailLabel"),
    emailPlaceholder: i18n("modules.editUserDrawerModal.emailPlaceholder"),
    updateUserButton: i18n("modules.editUserDrawerModal.updateUserButton"),
    cancelButton: i18n("modules.editUserDrawerModal.cancelButton"),
    rolesWarningText: i18n("modules.editUserDrawerModal.rolesWarningText"),
    inputsCheckWarningText: i18n(
      "modules.editUserDrawerModal.inputsCheckWarningText",
    ),
    userCreatedSuccessText: i18n(
      "modules.editUserDrawerModal.userCreatedSuccessText",
    ),
    detailsAndStatusErrorText: i18n(
      "modules.editUserDrawerModal.detailsAndStatusErrorText",
    ),
    detailsErrorText: i18n("modules.editUserDrawerModal.detailsErrorText"),
    statusErrorText: i18n("modules.editUserDrawerModal.statusErrorText"),
    userDataLoadingErrorText: i18n(
      "modules.editUserDrawerModal.userDataLoadingErrorText",
    ),
    activeStatusText: i18n("modules.editUserDrawerModal.activeStatusText"),
    inactiveStatusText: i18n("modules.editUserDrawerModal.inactiveStatusText"),
    initialStatusText: i18n("modules.editUserDrawerModal.initialStatusText"),
  };

  const [user, setUser] = useState<ZitadelUser | null>({
    id: "",
    username: "",
    given_name: "",
    family_name: "",
    email: "",
    email_is_verified: false,
    status: "INITIAL",
    roles: [],
    type: ZitadelUserType.HUMAN,
    nickname: "",
    language: "eng",
    chosen_timezone: "UTC",
    created_at: "",
    updated_at: "",
    preferred_login_name: "",
    login_names: [],
  });

  const isStatusModified = user?.status !== selectedUserFromUserList?.status;

  const closeDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "editUserDrawerModal",
      }),
    );
  };

  const handleEditUser = async () => {
    setUpdatingUser(true);

    if (user.roles.length === 0) {
      warningSnackbar(texts.rolesWarningText, 5000);
      setUpdatingUser(false);
      return;
    }

    const trimmedUser = trimUserInputs(user);
    const { isValid, errors } = validateUserInputs(trimmedUser);

    if (!isValid) {
      warningSnackbar(
        `${texts.inputsCheckWarningText}: ${errors.join(", ")}`,
        5000,
      );
      setUpdatingUser(false);
      return;
    }

    const [responseUser, responseStatus] = await Promise.all([
      patchUpdateUserUseCase(trimmedUser as ZitadelUser),
      isStatusModified && user.status !== "INITIAL"
        ? changeUserStatusUseCase(user.id, user.status)
        : Promise.resolve({ success: true }),
    ]);

    const updatingUserOk = responseUser.success;
    const updatingStatusOk = responseStatus.success;
    const bothOk = updatingUserOk && updatingStatusOk && responseUser.data;

    const dateToTriggerReload = new Date().toISOString();

    const isTheUserHimself = user.id === authContext.userId;
    const statusError =
      "error" in responseStatus && typeof responseStatus.error === "string"
        ? responseStatus.error
        : undefined;

    if (bothOk) {
      successSnackbar(texts.userCreatedSuccessText, 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));

      isTheUserHimself &&
        dispatch(updateLanguage(toISO2(user.language) as Language));
      isTheUserHimself &&
        sessionStorage.setItem("user-language", toISO2(user.language));
      isTheUserHimself &&
        dispatch(updateTimezone(user.chosen_timezone as Timezone));
    } else {
      const messages: string[] = [];
      if (!updatingUserOk && !updatingStatusOk) {
        if (!responseUser.error && !statusError) {
          messages.push(texts.detailsAndStatusErrorText);
        } else {
          messages.push(responseUser.error || texts.detailsErrorText);
          messages.push(statusError || texts.statusErrorText);
        }
      } else if (!updatingUserOk) {
        messages.push(responseUser.error || texts.detailsErrorText);
      } else if (!updatingStatusOk) {
        isTheUserHimself &&
          dispatch(updateLanguage(toISO2(user.language) as Language));
        isTheUserHimself &&
          sessionStorage.setItem("user-language", toISO2(user.language));
        isTheUserHimself &&
          dispatch(updateTimezone(user.chosen_timezone as Timezone));
        messages.push(statusError || texts.statusErrorText);
      }
      errorSnackbar(messages.join(". ") + ".", 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
    }

    setUpdatingUser(false);

    closeDrawer();
  };

  const returnStatusText = (status: string | null) => {
    switch (status) {
      case "ACTIVE":
        return texts.activeStatusText;
      case "INACTIVE":
        return texts.inactiveStatusText;
      case "INITIAL":
        return texts.initialStatusText;
      default:
        return texts.initialStatusText;
    }
  };

  const loadValidUserRoles = async () => {
    const roles = await getValidUserRolesUseCase();
    setRoleOptions(roles.data || []);
  };

  const loadUserData = async (userId: string) => {
    const user = await getSingleUserUseCase(userId);
    return user;
  };

  const fetchUser = async () => {
    const loadedUser = await loadUserData(selectedUserFromUserList.id);

    if (loadedUser?.success && loadedUser?.data) {
      setUser(loadedUser.data);
    } else {
      setUser(selectedUserFromUserList);
      warningSnackbar(texts.userDataLoadingErrorText, 5000);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      if (selectedUserFromUserList?.id) {
        const tasks = [fetchUser()];
        if (authContext?.permissions?.includes("manage-human-users")) {
          tasks.push(loadValidUserRoles());
        }

        await Promise.all(tasks);
      } else {
        await loadValidUserRoles();
        setUser(selectedUserFromUserList);
      }

      setLoading(false);
    };

    loadData();
  }, [selectedUserFromUserList]);

  return (
    <DrawerModal id={"editUserDrawerModal"}>
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={32}>
          <Title tag={"h2"} text={texts.editUserTitle} />
          {loading ? (
            <Spinner />
          ) : (
            <div className={styles[`${cssClassName}__content-wrapper`]}>
              <Input2025
                labelText={texts.userNameLabel}
                inputPlaceHolder={texts.userNamePlaceholder}
                inputValue={user.username || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, username: e.target.value })
                }
              />
              <Input2025
                labelText={texts.givenNameLabel}
                inputPlaceHolder={texts.givenNamePlaceholder}
                inputValue={user.given_name || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, given_name: e.target.value })
                }
              />
              <Input2025
                labelText={texts.familyNameLabel}
                inputPlaceHolder={texts.familyNamePlaceholder}
                inputValue={user.family_name || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, family_name: e.target.value })
                }
              />
              <RenderIfUserHasPermission permission="manage-human-users">
                <UserStatusWithSwitch
                  text={returnStatusText(user.status)}
                  setStatus={() =>
                    setUser({
                      ...user,
                      status: user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                    })
                  }
                  status={user.status}
                />
                <RoleSelector
                  user={user}
                  setUser={setUser}
                  roleOptions={roleOptions}
                />
              </RenderIfUserHasPermission>
              <Input2025
                labelText={texts.emailLabel}
                inputPlaceHolder={texts.emailPlaceholder}
                inputValue={user.email || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, email: e.target.value })
                }
              />
              <LanguageSelectorWithSearch
                selectedLanguage={user.language}
                setSelectedLanguage={(isoCode) =>
                  setUser({ ...user, language: isoCode })
                }
              />
              <TimezoneSelectorWithSearch
                selectedTimezone={user.chosen_timezone || "UTC"}
                setSelectedTimezone={(tz) =>
                  setUser({ ...user, chosen_timezone: tz })
                }
              />
              <TwoFactorAuthenticationButtonsContainer user={user} />
              <DrawerButtonWrapper>
                <ButtonWithLoader
                  fun={handleEditUser}
                  text={texts.updateUserButton}
                  variant={"full-width"}
                  loading={updatingUser}
                />
                <Button
                  fun={closeDrawer}
                  text={texts.cancelButton}
                  variant={["secondary", "full-width"]}
                />
              </DrawerButtonWrapper>
            </div>
          )}
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
};
